import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import { faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useMemo, useState } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, injectIntl, IntlShape } from "react-intl";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { usePropertyData } from "../../components/property-admin-data-provider";
import { usePropertyLocales } from "../../components/property-locale-provider";
import { BundleAPIPromiseClient } from "../../gen/witbooking/backoffice/bundle/v1/bundle_api_grpc_web_pb";
import {
  Bundle,
  BundleType,
  DeleteBundleRequest,
  GetBundlesRequest,
  Multimedia,
} from "../../gen/witbooking/backoffice/bundle/v1/bundle_api_pb";
import { useConfig } from "../../lib/config";
import { SECTION } from "../../lib/constants";
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";
import { DefaultTableRowActionsProps, Delete, Duplicate } from "../shared/actions/tableActions";
import { FormConfiguration } from "../shared/crud/form";
import SimpleCrud from "../shared/crud/simple-crud";
import { Image } from "../shared/widgets/ImageManager";
import {
  getAllowedInventoryLines,
  getFormConfiguration,
  getTravelWindowsFormConfiguration,
  getTravelWindowsTableColumns,
} from "./formConfiguration";
import { messages } from "./messages";
import { getSchema } from "./schema";
import { tableColumns } from "./tableColumns";

export interface CustomBundle extends Omit<Bundle.AsObject, "multimediaList"> {
  multimediaList: Image[];
}

function multimediaFromCustomMultimedia(multimediaList: Multimedia.AsObject[]): Image[] {
  return multimediaList.map(multimedia =>
    Object.assign(new File([""], multimedia.filename), {
      id: multimedia.id,
      url: multimedia.url,
      filename: multimedia.filename,
      translations:
        (multimedia.translationsList &&
          multimedia.translationsList.length &&
          multimedia.translationsList.reduce((accum, curr) => {
            const locale = curr.locale;
            return {
              ...accum,
              [locale]: {
                title: curr.title,
                description: curr.description,
              },
            };
          }, {})) ||
        {},
    })
  );
}

function packageFromCustomPackage({ multimediaList, ...props }: Bundle.AsObject): CustomBundle {
  return {
    ...props,
    multimediaList: multimediaFromCustomMultimedia(multimediaList),
  } as CustomBundle;
}

function BundleCrud({ propertyTicker, intl }: { propertyTicker: string; intl: IntlShape }) {
  const config = useConfig();
  const history = useHistory();
  const allowedLocales = usePropertyLocales();
  const propertyData = usePropertyData();

  // GRPC
  const grpcClient = useGrpcClient(BundleAPIPromiseClient);
  const getBundles = usePropertyGrpcCall(grpcClient, grpcClient.getBundles);
  const deleteBundle = usePropertyGrpcCall(grpcClient, grpcClient.deleteBundle);

  // Schema
  const customSchema = useMemo(() => getSchema(), []);

  // Form configuration
  const allowedInventoryLines = useMemo(() => getAllowedInventoryLines(propertyData), [
    propertyData,
  ]);
  const travelWindowsFormConfiguration = useMemo(() => getTravelWindowsFormConfiguration(), []);
  const travelWindowsTableColumns = useMemo(() => getTravelWindowsTableColumns(), []);
  const formConfiguration = useMemo<
    FormConfiguration<CustomBundle & { travelWindowSection?: string }>
  >(
    () =>
      getFormConfiguration(
        intl,
        allowedLocales,
        allowedInventoryLines,
        travelWindowsFormConfiguration,
        travelWindowsTableColumns,
        config
      ),
    [
      allowedLocales,
      allowedInventoryLines,
      intl,
      travelWindowsFormConfiguration,
      travelWindowsTableColumns,
      config,
    ]
  );

  // CRUD Methods
  const handleGetAll = useCallback(async (): Promise<CustomBundle[]> => {
    const request = new GetBundlesRequest();
    request.setType(BundleType.ENGINE);
    const response = await getBundles(request);
    return response.getBundleList().map(e => packageFromCustomPackage(e.toObject()));
  }, [getBundles]);

  const [reload, setReload] = useState(true);
  const [forceUpdate, setForceUpdate] = useState(0);

  const handleDelete = useCallback(
    async (entityId: string) => {
      const request = new DeleteBundleRequest();
      request.setBundleId(entityId);
      await deleteBundle(request);
      setForceUpdate(forceUpdate + 1);
    },
    [deleteBundle, forceUpdate]
  );

  const handleViewHistory = useCallback(
    (bundleId: string) => {
      history.push(`/${propertyTicker}/bundles-edit/${bundleId}`);
    },
    [history, propertyTicker]
  );

  const TableRowActions = useCallback(
    ({ row, rest }: DefaultTableRowActionsProps<any>) => {
      return (
        <Table.Cell {...rest}>
          <div className="inline-flex w-full items-center justify-center">
            <Duplicate onGoToDuplicate={id => handleViewHistory(id)} row={row} />
            <OverlayTrigger
              delay={{ show: 250, hide: 0 }}
              overlay={
                <Tooltip id="bundleEdit-tooltip">
                  <FormattedMessage {...messages.edit} />
                </Tooltip>
              }>
              <Button
                className="mr-2"
                size="sm"
                variant="light"
                onClick={() => handleViewHistory(row?.id)}>
                <FontAwesomeIcon icon={faPencilAlt} />
              </Button>
            </OverlayTrigger>
            <Delete
              rest={rest}
              onDelete={handleDelete}
              row={row}
              onReloadList={() => {
                console.info("reload");
                setReload(reload);
              }}
            />
          </div>
        </Table.Cell>
      );
    },
    [handleDelete, handleViewHistory, reload]
  );

  function BundleActions() {
    return (
      <Link className="btn btn-primary" to="bundles-new">
        <FormattedMessage {...messages.create} />
      </Link>
    );
  }

  return (
    <SimpleCrud<CustomBundle & { travelWindowSection?: string }>
      fixedActions={true}
      actions={BundleActions}
      actionsWidth={110}
      formConfiguration={formConfiguration}
      schema={customSchema}
      tableColumns={tableColumns}
      section={SECTION.BUNDLES}
      onGetAll={handleGetAll}
      onDelete={handleDelete}
      messages={messages}
      rowActions={props => <TableRowActions {...props} />}
      forceLoad={forceUpdate}
    />
  );
}

export default injectIntl(withAuthorizationVerifier(BundleCrud));
